import { wrap } from 'lodash-es';
import { distance, midpoint } from './math';
import {
  getEventClientCoords,
  testIsFakeMozMouseEvent,
  EventHandler,
  EventHandlerList,
  createEvent,
} from '../../utils/dom-events';

const Mode = {
  TOUCH: 1,
  MOUSE: 2,
};

const Key = {
  RETURN: 13,
  SPACE_BAR: 32,
};

function getMultiTouchMidpoint(event) {
  const [touch1, touch2] = event.touches;

  const { clientX: x1, clientY: y1 } = touch1;
  const { clientX: x2, clientY: y2 } = touch2;

  return midpoint(x1, y1, x2, y2);
}

function getMultiTouchDistance(event) {
  const [touch1, touch2] = event.touches;

  const { clientX: x1, clientY: y1 } = touch1;
  const { clientX: x2, clientY: y2 } = touch2;

  return distance(x1, y1, x2, y2);
}

function getTimeStamp() {
  return new Date().getTime();
}

function debugEvent(debugId, gestureName, event) {
  if (debugId !== null) {
    console.log(`gh :: ${debugId} :: ${gestureName}() :: ${event.type}`, event);
  }
}

function debug(debugId, message) {
  if (debugId !== null) {
    console.log(`gh ::${debugId} :: ${message}`);
  }
}

class GestureHelper {
  constructor(element, options = {}) {
    this.options = Object.assign({
      onPress: null,
      onPanMove: null,
      onPanEnd: null,
      onPinch: null,
      onClick: null,
      onRelease: null,
      onDoubleClick: null,

      debugId: null,
      passive: true,
      makeKeyboardClickable: false,
    }, options);

    this.bindMethods();
    this.wrapMethods();

    this.listeningHandlers = null;

    const passive = this.options.passive;

    this.startHandlers = new EventHandlerList([
      new EventHandler(element, 'touchstart', this.onPress, { passive }),
      new EventHandler(element, 'mousedown', this.onPress, { passive }),
    ]);
    this.touchHandlers = new EventHandlerList([
      new EventHandler(window, 'touchmove', this.onMove, { passive }),
      new EventHandler(window, 'touchend', this.onRelease),
    ]);

    this.mouseHandlers = new EventHandlerList([
      new EventHandler(window, 'mousemove', this.onMove, { passive }),
      new EventHandler(window, 'mouseup', this.onRelease),
    ]);

    if (this.options.onClick) {
      this.clickHandler = new EventHandler(element, 'click', this.onClickEvent);
    }

    if (this.options.makeKeyboardClickable) {
      this.keyUpHandler = new EventHandler(element, 'keyup', (event) => {
        if (event.keyCode === Key.SPACE_BAR || event.keyCode === Key.RETURN) {
          element.dispatchEvent(createEvent('click'));
        }
      });
    }
  }

  onClickEvent = (event) => {
    if (this.listeningHandlers) {
      return;
    }

    if (testIsFakeMozMouseEvent(event)) {
      return;
    }

    if (this.data.previousClick.eventTimeStamp === event.timeStamp) {
      return;
    }

    const now = getTimeStamp();
    const timeSinceLastRelease = now - this.data.previousClick.timeStamp;

    if (timeSinceLastRelease < 100) {
      return;
    }

    this.invokeCallback(this.options.onClick, event);
  }

  resetData() {
    // debug(this.options.debugId, 'resetData');
    this.data = {
      origin: { x: 0, y: 0 },
      current: { x: 0, y: 0 },
      offset: { x: 0, y: 0 },
      originOffset: { x: 0, y: 0 },
      isMultiTouch: false,
      hadMultiTouch: false,
      didPan: false,
      didPinch: false,
      previousClick: {
        consideredForDoubleClick: false,
        timeStamp: 0,
        eventTimeStamp: 0,
        coords: { x: 0, y: 0 },
        mode: null,
      },
      mode: null,
      pinch: {
        distance: {
          initial: 0,
          current: 0,
          previous: 0,
        },
      },
    };
  }

  bindMethods() {
    this.onPress = this.onPress.bind(this);
    this.onRelease = this.onRelease.bind(this);
    this.onMove = this.onMove.bind(this);
    this.registerReleaseEvents = this.registerReleaseEvents.bind(this);
    this.unregisterReleaseEvents = this.unregisterReleaseEvents.bind(this);
  }

  wrapMethods() {
    function wrapper(wrapped, event) {
      if (testIsFakeMozMouseEvent(event)) {
        return;
      }
      const coords = getEventClientCoords(event);
      wrapped(event, coords);
    }

    this.onPress = wrap(this.onPress, wrapper);
    this.onRelease = wrap(this.onRelease, wrapper);
    this.onMove = wrap(this.onMove, wrapper);
  }

  registerReleaseEvents() {
    switch (this.data.mode) {
      case Mode.TOUCH:
        this.listeningHandlers = this.touchHandlers;
        break;
      case Mode.MOUSE:
        this.listeningHandlers = this.mouseHandlers;
        break;
      default:
        break;
    }

    this.listeningHandlers.start();
  }

  unregisterReleaseEvents() {
    if (this.listeningHandlers !== null) {
      this.listeningHandlers.stop();
      this.listeningHandlers = null;
    }
  }

  onPress(event, coords) {
    if (this.listeningHandlers === null) {
      debugEvent(this.options.debugId, 'onPress', event);

      if (event.timeStamp === 0) {
        // Mobile Safari synthetic mouse event?
        return;
      }

      const mode = event.type === 'touchstart' ? Mode.TOUCH : Mode.MOUSE;

      if (
        mode === Mode.MOUSE &&
        // this.data.previousClick === Mode.TOUCH &&
        this.data.previousClick.eventTimeStamp === event.timeStamp
      ) {
        // Synthetic mouse event previously handled as a click?
        return;
      }

      this.data.mode = mode;
      this.data.isMultiTouch = false;
      this.data.hadMultiTouch = false;
      this.didPan = false;

      this.resetToOrigin(coords.clientX, coords.clientY);
      this.registerReleaseEvents(event);
      this.invokeCallback(this.options.onPress, event);
    }
  }

  onMove(event, coords) {
    debugEvent(this.options.debugId, 'onMove', event);

    const touchCount = (event.touches && event.touches.length) || 1;
    const isFirstPan = this.data.didPan === false;
    this.data.didPan = true;

    switch (touchCount) {
      case 1:
        if (this.data.isMultiTouch) {
          this.resetToOrigin(coords.clientX, coords.clientY);
          this.data.isMultiTouch = false;
        } else {
          this.handleSinglePointMove(event, coords, isFirstPan);
        }
        break;
      case 2:

        if (this.data.isMultiTouch) {
          this.handleMultiPointMove(event);
        } else {
          const origin = getMultiTouchMidpoint(event);
          this.resetToOrigin(origin.x, origin.y);

          const initialDistance = getMultiTouchDistance(event);
          this.data.pinch.distance.initial = initialDistance;
          this.data.pinch.distance.previous = initialDistance;
          this.data.pinch.distance.current = initialDistance;
          this.data.isMultiTouch = true;
          this.data.hadMultiTouch = true;
          this.data.didPinch = true;
        }

        break;
      default:
        break;
    }
  }

  onRelease(event) {
    debugEvent(this.options.debugId, 'onRelease', event);
    const mode = this.data.mode;
    const previousClick = this.data.previousClick;
    const moveThreshold = mode === Mode.MOUSE ? 5 : 20;
    const now = getTimeStamp();

    if (mode === Mode.TOUCH && event.touches.length > 0) {
      // User had 2+ touches - went down to 1+
    } else {
      this.unregisterReleaseEvents();

      const isConsideredClick =
        this.data.hadMultiTouch === false &&
        Math.abs(this.data.originOffset.x) <= moveThreshold &&
        Math.abs(this.data.originOffset.y) <= moveThreshold;

      if (this.data.didPan) {
        this.invokeCallback(this.options.onPanEnd, event);
      }

      if (isConsideredClick) {
        if (
          previousClick.mode === this.data.mode &&
          now - previousClick.timeStamp <= 300
        ) {
          this.invokeCallback(this.options.onDoubleClick, event);
          previousClick.timeStamp = 0;
        } else {
          this.invokeCallback(this.options.onClick, event);
          previousClick.timeStamp = now;
        }

        const current = this.data.current;
        previousClick.coords.x = current.x;
        previousClick.coords.y = current.y;
        previousClick.mode = this.data.mode;
        previousClick.eventTimeStamp = event.timeStamp;
      }

      this.invokeCallback(this.options.onRelease, event);
    }
  }

  handleSinglePointMove(event, coords, isFirstPan) {
    this.updateOriginOffset(coords.clientX, coords.clientY);
    if (isFirstPan === true && this.data.originOffset.x === 0 && this.data.originOffset.y === 0) {
      this.data.didPan = false;
    } else {
      this.invokeCallback(this.options.onPanMove, event);
    }
  }

  handleMultiPointMove(event) {
    const center = getMultiTouchMidpoint(event);
    this.updateOriginOffset(center.x, center.y);

    this.data.pinch.distance.previous = this.data.pinch.distance.current;
    this.data.pinch.distance.current = getMultiTouchDistance(event);

    this.invokeCallback(this.options.onPinch, event);
  }

  invokeCallback(callback, event) {
    if (callback !== null) {
      callback(event, this.data);
    }
  }

  updateOriginOffset(newOriginX, newOriginY) {
    const { current, offset, origin, originOffset } = this.data;

    offset.x = newOriginX - current.x;
    offset.y = newOriginY - current.y;

    // LOL @ negative 0...
    const originOffsetX = origin.x - newOriginX;
    const originOffsetY = origin.y - newOriginY;
    originOffset.x = originOffsetX === 0 ? 0 : originOffsetX * -1;
    originOffset.y = originOffsetY === 0 ? 0 : originOffsetY * -1;

    current.x = newOriginX;
    current.y = newOriginY;
  }

  resetToOrigin(x, y) {
    const { origin, current, offset, originOffset } = this.data;
    origin.x = x;
    origin.y = y;
    current.x = x;
    current.y = y;
    offset.x = 0;
    originOffset.x = 0;
    offset.y = 0;
    originOffset.y = 0;
  }

  start() {
    // debug(this.options.debugId, 'start');
    this.resetData();
    this.startHandlers.start();

    if (this.clickHandler) {
      this.clickHandler.start();
    }

    if (this.keyUpHandler) {
      this.keyUpHandler.start();
    }

    return this;
  }

  stop() {
    debug(this.options.debugId, 'stop');
    this.startHandlers.stop();
    this.touchHandlers.stop();
    this.mouseHandlers.stop();

    if (this.clickHandler) {
      this.clickHandler.stop();
    }

    if (this.keyUpHandler) {
      this.keyUpHandler.stop();
    }

    this.unregisterReleaseEvents();
    return this;
  }

  destroy() {
    debug(this.options.debugId, 'destroy');
    this.stop();

    this.startHandlers = null;
    this.touchHandlers = null;
    this.mouseHandlers = null;
    this.clickHandler = null;
    this.keyUpHandler = null;
    this.listeningHandlers = null;
  }
}

class Click extends GestureHelper {
  constructor(element, onClick) {
    super(element, { onClick });
  }
}

class GestureList {
  constructor(items) {
    this.items = items;
  }
  start() {
    this.items.forEach(item => item.start());
    return this;
  }
  stop() {
    this.items.forEach(item => item.stop());
    return this;
  }
}


export default GestureHelper;

export {
  GestureList,
  Click,
};



// WEBPACK FOOTER //
// ./V7/syos/utils/GestureHelper.js